import { Button, Card, Col, Row, Typography, Spin, Table, Tag, Empty } from "antd";
import { ReloadOutlined, ShoppingCartOutlined } from "@ant-design/icons";
import React, { useEffect, useState } from "react";
import { toGetLastAutoReport, toGetAutoOrderHistory } from "../APIs";
import { toConvertDate } from "../../../Utils/Utils";

const { Title, Text } = Typography;

const AutoOrderDashboard = () => {
  const [report, setReport] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = () => {
    setLoading(true);
    Promise.all([
      toGetLastAutoReport(),
      toGetAutoOrderHistory(),
    ]).then(([lastReport, reports]) => {
      setReport(lastReport);
      setHistoryData(reports || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  const renderSupplier = (suppliers, index) => {
    if (!suppliers || suppliers.length <= index) {
      return <Text type="secondary">—</Text>;
    }
    const supplier = suppliers[index];
    return (
      <div>
        <div>{supplier.providerName}</div>
        <Text type="secondary">{parseFloat(supplier.priceHT || 0).toFixed(3)} DT</Text>
      </div>
    );
  };

  const detailColumns = [
    {
      title: "Vendeur",
      key: "seller",
      render: (_, record) => (record.seller ? record.seller.username : "N/A"),
    },
    {
      title: "Nb articles",
      key: "nbArticles",
      render: (_, record) => record.articles.length,
    },
    {
      title: "Total TTC",
      key: "total",
      render: (_, record) => {
        const total = record.articles.reduce(
          (sum, a) => sum + parseFloat(a.unitPriceTTC || 0) * parseFloat(a.qty || 0),
          0
        );
        return total.toFixed(3) + " DT";
      },
    },
  ];

  const articleColumns = [
    { title: "Référence", dataIndex: "reference", key: "reference" },
    { title: "Article", dataIndex: "articleName", key: "articleName" },
    { title: "Stock avant", dataIndex: "stockBefore", key: "stockBefore" },
    { title: "Ventes 30j", dataIndex: "monthlySales", key: "monthlySales" },
    { title: "Qté à commander", dataIndex: "qty", key: "qty" },
    {
      title: "Fournisseur 1",
      key: "supplier1",
      render: (_, record) => renderSupplier(record.suppliers, 0),
    },
    {
      title: "Fournisseur 2",
      key: "supplier2",
      render: (_, record) => renderSupplier(record.suppliers, 1),
    },
    {
      title: "Fournisseur 3",
      key: "supplier3",
      render: (_, record) => renderSupplier(record.suppliers, 2),
    },
  ];

  const historyColumns = [
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (v) => toConvertDate(v),
    },
    {
      title: "Statut",
      dataIndex: "status",
      key: "status",
      render: (v) => (
        <Tag color={v === "success" ? "green" : "orange"}>
          {v === "success" ? "Succès" : "Partiel"}
        </Tag>
      ),
    },
    { title: "Vendeurs", dataIndex: "totalOrders", key: "totalOrders" },
    { title: "Articles", dataIndex: "totalArticles", key: "totalArticles" },
  ];

  const expandedRowRender = (group) => {
    return (
      <Table
        columns={articleColumns}
        dataSource={group.articles}
        rowKey={(r) => r.articleId + "-" + Math.random()}
        pagination={false}
        size="small"
      />
    );
  };

  return (
    <div style={{ padding: "2vh" }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: "2vh" }}>
        <Col>
          <Title level={4}>
            <ShoppingCartOutlined style={{ marginRight: 8 }} />
            Commandes automatiques
          </Title>
        </Col>
        <Col>
          <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>
            Rafraîchir
          </Button>
        </Col>
      </Row>

      {loading ? (
        <div style={{ textAlign: "center", padding: "10vh" }}>
          <Spin size="large" />
        </div>
      ) : report ? (
        <>
          <Row gutter={[16, 16]} style={{ marginBottom: "2vh" }}>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Dernière exécution"
                  value={toConvertDate(report.createdAt)}
                  valueStyle={{ fontSize: 16 }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic title="Vendeurs concernés" value={report.totalOrders} />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic title="Articles commandés" value={report.totalArticles} />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Statut"
                  valueRender={() => (
                    <Tag color={report.status === "success" ? "green" : "orange"} style={{ fontSize: 14, padding: "4px 12px" }}>
                      {report.status === "success" ? "Succès" : "Partiel"}
                    </Tag>
                  )}
                />
              </Card>
            </Col>
          </Row>

          <Card title="Résumé des articles à commander par vendeur" style={{ marginBottom: "2vh" }}>
            {report.details && report.details.length > 0 ? (
              <Table
                columns={detailColumns}
                dataSource={report.details}
                rowKey={(r, i) => (r.seller ? "seller-" + r.seller.id : "seller-none-" + i)}
                expandable={{
                  expandedRowRender,
                  rowExpandable: () => true,
                }}
                pagination={false}
              />
            ) : (
              <Empty description="Aucun article à commander" />
            )}
          </Card>

          <Card title="Historique des exécutions">
            <Table
              columns={historyColumns}
              dataSource={historyData}
              rowKey="id"
              pagination={{ pageSize: 10 }}
              size="small"
            />
          </Card>
        </>
      ) : (
        <Card>
          <Empty description="Aucune commande automatique n'a encore été exécutée">
            <Text type="secondary">
              La commande automatique s'exécute chaque nuit à minuit.
              <br />
              Si aucune exécution n'apparaît, vérifiez que le cron est bien configuré.
            </Text>
          </Empty>
        </Card>
      )}
    </div>
  );
};

const Statistic = ({ title, value, valueStyle, valueRender }) => (
  <div>
    <Text type="secondary" style={{ fontSize: 13 }}>{title}</Text>
    <div style={{ marginTop: 4 }}>
      {valueRender ? valueRender() : <Text strong style={{ fontSize: 22, ...valueStyle }}>{value}</Text>}
    </div>
  </div>
);

export default AutoOrderDashboard;
